import { inject, Injectable, InjectionToken } from '@angular/core';
import { ConfigModel, DesignerPageComponentType, GsStructComponent, IUiElements, PageComponentType } from '../../models';
import { SchemaService } from '../forms';
import { BasicComponentDefinition, ComponentsHelper } from './components.service';

/** All possible groups for page components */
export type PageComponentGroup = 'basic' | 'content' | 'shop' | 'account' | 'detail';

export interface PageComponentDefinition extends BasicComponentDefinition<PageComponentGroup> {
  /** Is this component suited with a userControl in the backend and maby also has some backend-settings? */
  backendComponent: boolean;
  /** Is this component still in development and shouldnt be used yet? (Disabled for adding newly into pages, components already on page will work) */
  wip?: boolean;
}

/**
 * Token providing the page-component registry. The concrete dictionary lives in
 * `page.components.registry.ts` (outside the services barrel) and is wired up via
 * `PAGE_COMPONENTS_REGISTRY_PROVIDER` in each app config. Splitting it out breaks an
 * SSR import cycle (services → template → main barrel) that left template component
 * classes undefined during Vite SSR.
 */
export const PAGE_COMPONENTS_REGISTRY = new InjectionToken<Record<DesignerPageComponentType, PageComponentDefinition>>(
  'PAGE_COMPONENTS_REGISTRY',
  { factory: () => ({}) as Record<DesignerPageComponentType, PageComponentDefinition> }
);

@Injectable({
  providedIn: 'root'
})
export class PageComponentsHelper extends ComponentsHelper<DesignerPageComponentType, PageComponentDefinition, PageComponentGroup> {
  /** The priority of the groups to display them in the right order in the sidemenu (lower number = higher priority) */
  override groupPriorities: Record<PageComponentGroup, number> = {
    basic: 1,
    content: 2,
    shop: 3,
    account: 4,
    detail: 5
  };

  // Services
  /** The components to display in the sidemenu */
  override components: Record<DesignerPageComponentType, PageComponentDefinition> = inject(PAGE_COMPONENTS_REGISTRY);

  // Methods
  /**
   * Returns the initial settings for a newly created component.
   * Applies the config priority chain: Designer/Page/Component > Schema-Fallback.
   * When a component is saved, these defaults are persisted — later config changes
   * do not override already saved component data.
   * @param type of the component
   * @param config the config to get the default settings from
   * @returns the default settings for the component
   */
  getComponentDefaultSettings(type: DesignerPageComponentType, config?: ConfigModel): GsStructComponent<unknown> | undefined {
    const configDefaultSettings: GsStructComponent<unknown> | undefined = config?.components ? config.components[type] : undefined;
    const data = this.components[type]?.formSchema?.fields?.reduce((acc, field) => {
      if (SchemaService.isGsFormController(field)) {
        const configDefault = configDefaultSettings
          ? configDefaultSettings[field.formControlName as keyof GsStructComponent<unknown>]
          : undefined;

        /** For group fields matching a UI-Element key (e.g. 'buttons'), prefer the runtime uiElements config over the hardcoded schema default */
        let resolvedDefault = field.default;
        if ('type' in field && field.type === 'group' && config?.uiElements) {
          const uiValue = config.uiElements[field.formControlName as keyof IUiElements];
          if (uiValue != null) {
            resolvedDefault = uiValue;
          }
        }

        if (configDefault != null || resolvedDefault != null) {
          (acc as unknown as Record<string, unknown>)[field.formControlName] = configDefault ?? resolvedDefault;
        }
      }
      return acc;
    }, {} as GsStructComponent<any>);
    return data;
  }

  isBackendComponent(type: PageComponentType): boolean {
    if (type in this.components) return this.components[type as DesignerPageComponentType]?.backendComponent ?? false;
    return false;
  }
}
